/**
 * EGMMS V3 - Dashboard Service
 * 대시보드 통계 및 데이터 서비스
 */

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { Project } from '../types';

/**
 * 대시보드 통계 타입
 */
export interface DashboardStats {
  totalProjectCount: number;      // 전체 프로젝트
  inProgressProjectCount: number; // 진행중 프로젝트
  planningProjectCount: number;   // 기획중 프로젝트
  completedProjectCount: number;  // 완료된 프로젝트
  moldCount: number;               // 등록된 금형
  monthlyGrowth: number;           // 이번달 증가율
}

/**
 * 1. 전체 프로젝트 수 조회
 */
export const getProjectCount = async (): Promise<number> => {
  try {
    const projectsRef = collection(db, 'projects');
    const snapshot = await getDocs(projectsRef);
    return snapshot.size;
  } catch (error) {
    console.error('프로젝트 수 조회 실패:', error);
    return 0;
  }
};

/**
 * 1-1. 진행중 프로젝트 수 조회
 */
export const getInProgressProjectCount = async (): Promise<number> => {
  try {
    const projectsRef = collection(db, 'projects');
    const q = query(projectsRef, where('status', '==', '진행중'));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('진행중 프로젝트 수 조회 실패:', error);
    return 0;
  }
};

/**
 * 1-2. 기획중 프로젝트 수 조회
 */
export const getPlanningProjectCount = async (): Promise<number> => {
  try {
    const projectsRef = collection(db, 'projects');
    const q = query(projectsRef, where('status', '==', '계획'));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('기획중 프로젝트 수 조회 실패:', error);
    return 0;
  }
};

/**
 * 1-3. 완료된 프로젝트 수 조회
 */
export const getCompletedProjectCount = async (): Promise<number> => {
  try {
    const projectsRef = collection(db, 'projects');
    const q = query(projectsRef, where('status', '==', '완료'));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('완료된 프로젝트 수 조회 실패:', error);
    return 0;
  }
};

/**
 * 2. 전체 금형 수 조회
 */
export const getMoldCount = async (): Promise<number> => {
  try {
    const moldsRef = collection(db, 'molds');
    const snapshot = await getDocs(moldsRef);
    return snapshot.size;
  } catch (error) {
    console.error('금형 수 조회 실패:', error);
    return 0;
  }
};

/**
 * 3. 완료된 작업 수 조회 (이번 달 히스토리 기준)
 */
export const getCompletedTaskCount = async (): Promise<number> => {
  try {
    // 이번 달 시작일
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const historiesRef = collection(db, 'moldHistories');
    const q = query(
      historiesRef,
      where('historyDate', '>=', Timestamp.fromDate(startOfMonth))
    );
    
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('완료된 작업 수 조회 실패:', error);
    return 0;
  }
};

/**
 * 4. 이번 달 증가율 계산 (프로젝트 기준)
 */
export const getMonthlyGrowth = async (): Promise<number> => {
  try {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    
    const projectsRef = collection(db, 'projects');
    
    // 이번 달 생성된 프로젝트
    const thisMonthQuery = query(
      projectsRef,
      where('createdAt', '>=', Timestamp.fromDate(startOfMonth))
    );
    const thisMonthSnapshot = await getDocs(thisMonthQuery);
    const thisMonthCount = thisMonthSnapshot.size;
    
    // 지난 달 생성된 프로젝트
    const lastMonthQuery = query(
      projectsRef,
      where('createdAt', '>=', Timestamp.fromDate(startOfLastMonth)),
      where('createdAt', '<=', Timestamp.fromDate(endOfLastMonth))
    );
    const lastMonthSnapshot = await getDocs(lastMonthQuery);
    const lastMonthCount = lastMonthSnapshot.size;
    
    // 증가율 계산
    if (lastMonthCount === 0) {
      return thisMonthCount > 0 ? 100 : 0;
    }
    
    const growth = ((thisMonthCount - lastMonthCount) / lastMonthCount) * 100;
    return Math.round(growth * 10) / 10; // 소수점 1자리
  } catch (error) {
    console.error('월별 증가율 조회 실패:', error);
    return 0;
  }
};

/**
 * 5. 최근 프로젝트 목록 조회
 */
export const getRecentProjects = async (limitCount: number = 5): Promise<Project[]> => {
  try {
    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef,
      orderBy('updatedAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    const projects: Project[] = [];
    
    snapshot.forEach((doc) => {
      projects.push({
        id: doc.id,
        ...doc.data(),
      } as Project);
    });
    
    // 각 프로젝트의 실제 금형 수 계산
    const projectsWithMoldCount = await Promise.all(
      projects.map(async (project) => {
        try {
          const moldsQuery = query(
            collection(db, 'molds'),
            where('projectId', '==', project.id)
          );
          const moldsSnapshot = await getDocs(moldsQuery);
          return {
            ...project,
            moldCount: moldsSnapshot.size
          };
        } catch (error) {
          console.error(`프로젝트 ${project.id} 금형 수 조회 실패:`, error);
          return {
            ...project,
            moldCount: 0
          };
        }
      })
    );
    
    return projectsWithMoldCount;
  } catch (error) {
    console.error('최근 프로젝트 조회 실패:', error);
    return [];
  }
};

/**
 * 6. 대시보드 전체 통계 한번에 조회
 */
export const getDashboardStats = async (): Promise<DashboardStats> => {
  try {
    const [
      totalProjectCount,
      inProgressProjectCount,
      planningProjectCount,
      completedProjectCount,
      moldCount,
      monthlyGrowth,
    ] = await Promise.all([
      getProjectCount(),
      getInProgressProjectCount(),
      getPlanningProjectCount(),
      getCompletedProjectCount(),
      getMoldCount(),
      getMonthlyGrowth(),
    ]);
    
    return {
      totalProjectCount,
      inProgressProjectCount,
      planningProjectCount,
      completedProjectCount,
      moldCount,
      monthlyGrowth,
    };
  } catch (error) {
    console.error('대시보드 통계 조회 실패:', error);
    return {
      totalProjectCount: 0,
      inProgressProjectCount: 0,
      planningProjectCount: 0,
      completedProjectCount: 0,
      moldCount: 0,
      monthlyGrowth: 0,
    };
  }
};
